import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Phone, Mail, Calendar, MapPin, DollarSign, HelpCircle, Building } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LaundryAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Quick reply options
  const quickReplies = [
    { icon: '🧺', label: 'Wash & Fold', message: 'Tell me about your Wash & Fold service' },
    { icon: '💰', label: 'Pricing', message: 'How much does wash and fold cost?' },
    { icon: '🚚', label: 'Pickup & Delivery', message: 'Do you offer pickup and delivery?' },
    { icon: '📍', label: 'Service Areas', message: 'What areas do you serve?' },
    { icon: '🏪', label: 'Drop-Off', message: 'Can I drop off my laundry?' },
    { icon: '📅', label: 'Book a Pickup', action: 'book' },
  ];

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Track analytics
  const trackEvent = async (eventType) => {
    try {
      await axios.post(`${API}/api/chatbot/analytics/track`, { event_type: eventType });
    } catch (e) {
      // Silently fail analytics
    }
  };

  // Open chat
  const handleOpen = () => {
    setIsOpen(true);
    trackEvent('chat_opened');
    
    // Show welcome message if no messages
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm your Laundry Assistant. How can I help with your laundry today?",
        timestamp: new Date().toISOString()
      }]);
    }
  };

  // Send message to chatbot
  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/api/chatbot/chat`, {
        message: messageText,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chatbot error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I'm sorry, I'm having trouble responding right now. Please try again or contact us directly at 762-436-2067.",
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle quick reply click
  const handleQuickReply = (reply) => {
    if (reply.action === 'book') {
      trackEvent('booking_clicked');
      const bookingSection = document.getElementById('book-now');
      if (bookingSection) {
        setIsOpen(false);
        bookingSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      sendMessage(reply.message);
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputMessage);
  };

  // Handle contact action
  const handleContact = (type) => {
    trackEvent('contact_clicked');
    if (type === 'phone') {
      window.location.href = 'tel:762-436-2067';
    } else if (type === 'email') {
      window.location.href = 'mailto:familylaundry5265@gmail.com';
    }
  };

  // Navigate to section
  const navigateToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      setIsOpen(false);
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          data-testid="chatbot-toggle-button"
          className="fixed bottom-6 right-6 z-50 bg-teal-600 hover:bg-teal-700 text-white rounded-full p-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 flex items-center gap-2 group"
          aria-label="Open Laundry Assistant"
        >
          <MessageCircle size={24} />
          <span className="hidden sm:inline font-medium pr-1">Laundry Assistant</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-96 h-[100dvh] sm:h-[600px] sm:max-h-[80vh] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-4 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="font-bold text-lg">Laundry Assistant</h3>
              <p className="text-xs text-teal-100">How can I help with your laundry today?</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chatbot-close-button"
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-teal-600 text-white rounded-br-md'
                      : msg.isError
                      ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 size={16} className="animate-spin" />
                    <span className="text-sm">Typing...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length <= 1 && !isLoading && (
            <div className="p-3 bg-white border-t border-gray-100 flex-shrink-0">
              <p className="text-xs text-gray-500 mb-2">What can I help you with?</p>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickReply(reply)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-full text-xs font-medium transition-colors"
                  >
                    <span>{reply.icon}</span>
                    <span>{reply.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {messages.length > 1 && !isLoading && (
            <div className="p-2 bg-white border-t border-gray-100 flex-shrink-0">
              <div className="flex gap-2 overflow-x-auto pb-1">
                <button
                  onClick={() => navigateToSection('book-now')}
                  className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-full text-xs font-medium transition-colors whitespace-nowrap"
                >
                  <Calendar size={12} />
                  Book Pickup
                </button>
                <button
                  onClick={() => navigateToSection('services')}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
                >
                  <DollarSign size={12} />
                  View Pricing
                </button>
                <button
                  onClick={() => handleContact('phone')}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
                >
                  <Phone size={12} />
                  Call Us
                </button>
              </div>
            </div>
          )}

          {/* Input Area */}
          <form onSubmit={handleSubmit} className="p-3 bg-white border-t border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                data-testid="chatbot-input"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading || !inputMessage.trim()}
                data-testid="chatbot-send-button"
                className="p-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white rounded-full transition-colors"
                aria-label="Send message"
              >
                <Send size={18} />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default LaundryAssistant;
